import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { TipTargeting } from '../utils/tipTargeting';
import { OnScoreTipCoordinates } from '../utils/onScoreTipCoordinates';
import '../styles/tips.css';

const OnScoreTipOverlay = ({ 
  osmdInstance, 
  musicxmlId, 
  isVisible, 
  visibleParts = [],
  onMeasureClick,
  scoreDisplayRef, // Add reference to ScoreDisplay component
  refreshTrigger = 0, // Add prop to trigger reload
  isInPlacementMode = false, // Add prop to dim existing tips during placement
  onTipsLoaded = null // Callback to notify parent about tip availability
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [tips, setTips] = useState([]);
  const [coordinateSystem, setCoordinateSystem] = useState(null);
  const [hoveredTip, setHoveredTip] = useState(null);
  const [containerOffset, setContainerOffset] = useState({ x: 0, y: 0 });
  const renderTimeoutRef = useRef(null);
  const audioInstancesRef = useRef(new Map()); // Store audio instances persistently
  const audioStatesRef = useRef(new Map()); // Store audio states (playing, paused, stopped)

  // Initialize coordinate system
  useEffect(() => {
    if (osmdInstance && isVisible) {
      const coords = new OnScoreTipCoordinates(osmdInstance);
      setCoordinateSystem(coords);
      
      return () => {
        coords.dispose();
      };
    }
  }, [osmdInstance, isVisible]);

  // Calculate offset between overlay position and score container
  useEffect(() => {
    if (scoreDisplayRef?.current && osmdInstance?.container && isVisible) {
      const updateOffset = () => {
        try {
          const scoreContainer = scoreDisplayRef.current.getContainer();
          if (scoreContainer) {
            const containerRect = scoreContainer.getBoundingClientRect();
            
            // The offset we need to apply to position tips correctly
            setContainerOffset({
              x: containerRect.left,
              y: containerRect.top
            });
          }
        } catch (error) {
          console.error('Error calculating container offset:', error);
        }
      };
      
      updateOffset();
      
      // Update offset on resize or scroll
      window.addEventListener('resize', updateOffset);
      window.addEventListener('scroll', updateOffset);
      
      return () => {
        window.removeEventListener('resize', updateOffset);
        window.removeEventListener('scroll', updateOffset);
      };
    }
  }, [scoreDisplayRef, osmdInstance, isVisible]);

  // Load on-score tips - always load to check availability, regardless of visibility
  useEffect(() => {
    if (user?.id && musicxmlId) {
      loadOnScoreTips();
    }
  }, [user, musicxmlId, refreshTrigger]);

  // Update tip positions when part visibility changes
  useEffect(() => {
    if (coordinateSystem && tips.length > 0) {
      updateTipPositions();
    }
  }, [visibleParts, coordinateSystem, tips.length]);

  // Audio management functions
  const getAudioClipKey = (tipId, clipName) => `${tipId}-${clipName}`;
  
  const getAudioState = (tipId, clipName) => {
    const key = getAudioClipKey(tipId, clipName);
    return audioStatesRef.current.get(key) || 'stopped';
  };
  
  const setAudioState = (tipId, clipName, state) => {
    const key = getAudioClipKey(tipId, clipName);
    audioStatesRef.current.set(key, state);
  };
  
  const getAudioInstance = async (tipId, clipName) => {
    const key = getAudioClipKey(tipId, clipName);
    let audio = audioInstancesRef.current.get(key);
    
    if (!audio) {
      // Create new audio instance
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const params = new URLSearchParams({
          operation: 'getAudioClipsByNames',
          names: clipName
        });
        const response = await fetch(`${API_BASE}/api/audio-clips?${params}`);
        
        if (response.ok) {
          const clipsData = await response.json();
          const clip = clipsData.find(c => c.name === clipName);
          
          if (clip && clip.file_url) {
            audio = new Audio(clip.file_url);
            audioInstancesRef.current.set(key, audio);
            
            // Set up event listeners
            audio.addEventListener('ended', () => {
              setAudioState(tipId, clipName, 'stopped');
              updateAudioButton(tipId, clipName);
            });
            
            audio.addEventListener('error', () => {
              setAudioState(tipId, clipName, 'error');
              updateAudioButton(tipId, clipName);
              setTimeout(() => {
                setAudioState(tipId, clipName, 'stopped');
                updateAudioButton(tipId, clipName);
              }, 2000);
            });
          }
        }
      } catch (error) {
        console.error('Error creating audio instance:', error);
      }
    }
    
    return audio;
  };
  
  const updateAudioButton = (tipId, clipName) => {
    const key = getAudioClipKey(tipId, clipName);
    const state = getAudioState(tipId, clipName);
    const buttonElement = document.querySelector(`[data-audio-key="${key}"]`);
    
    if (buttonElement) {
      switch (state) {
        case 'playing':
          buttonElement.textContent = `⏸️ ${clipName}`;
          buttonElement.style.background = '#16a34a';
          buttonElement.title = `Pause: ${clipName}`;
          break;
        case 'paused':
          buttonElement.textContent = `▶️ ${clipName}`;
          buttonElement.style.background = '#f59e0b';
          buttonElement.title = `Resume: ${clipName}`;
          break;
        case 'error':
          buttonElement.textContent = `❌ ${clipName}`;
          buttonElement.style.background = '#dc2626';
          buttonElement.title = `Error: ${clipName}`;
          break;
        default: // stopped
          buttonElement.textContent = `▶️ ${clipName}`;
          buttonElement.style.background = '#3b82f6';
          buttonElement.title = `Play: ${clipName}`;
          break;
      }
    }
    
    // Also update stop button visibility
    updateStopButton(tipId, clipName);
  };
  
  const handleAudioClick = async (tipId, clipName) => {
    const audio = await getAudioInstance(tipId, clipName);
    const currentState = getAudioState(tipId, clipName);
    
    if (!audio) {
      setAudioState(tipId, clipName, 'error');
      updateAudioButton(tipId, clipName);
      return;
    }
    
    try {
      if (currentState === 'playing') {
        // Pause
        audio.pause();
        setAudioState(tipId, clipName, 'paused');
      } else if (currentState === 'paused') {
        // Resume
        await audio.play();
        setAudioState(tipId, clipName, 'playing');
      } else {
        // Play from beginning
        audio.currentTime = 0;
        await audio.play();
        setAudioState(tipId, clipName, 'playing');
      }
    } catch (error) {
      console.error('Error controlling audio:', error);
      setAudioState(tipId, clipName, 'error');
    }
    
    updateAudioButtons(tipId, clipName);
  };

  const handleAudioStop = async (tipId, clipName) => {
    const currentState = getAudioState(tipId, clipName);
    
    // Only allow stopping if audio is playing or paused
    if (currentState !== 'playing' && currentState !== 'paused') {
      return;
    }
    
    const audio = await getAudioInstance(tipId, clipName);
    
    if (!audio) {
      return;
    }
    
    try {
      audio.pause();
      audio.currentTime = 0;
      setAudioState(tipId, clipName, 'stopped');
      updateAudioButton(tipId, clipName);
      updateStopButton(tipId, clipName);
    } catch (error) {
      console.error('Error stopping audio:', error);
    }
  };

  const handleAudioPlay = async (tipId, clipName) => {
    const audio = await getAudioInstance(tipId, clipName);
    
    if (!audio) {
      setAudioState(tipId, clipName, 'error');
      updateAudioButtons(tipId, clipName);
      return;
    }
    
    try {
      audio.currentTime = 0;
      await audio.play();
      setAudioState(tipId, clipName, 'playing');
    } catch (error) {
      console.error('Error playing audio:', error);
      setAudioState(tipId, clipName, 'error');
    }
    
    updateAudioButtons(tipId, clipName);
  };

  const handleAudioPause = async (tipId, clipName) => {
    const audio = await getAudioInstance(tipId, clipName);
    
    if (!audio) {
      return;
    }
    
    try {
      audio.pause();
      setAudioState(tipId, clipName, 'paused');
    } catch (error) {
      console.error('Error pausing audio:', error);
    }
    
    updateAudioButtons(tipId, clipName);
  };

  const handleAudioResume = async (tipId, clipName) => {
    const audio = await getAudioInstance(tipId, clipName);
    
    if (!audio) {
      return;
    }
    
    try {
      await audio.play();
      setAudioState(tipId, clipName, 'playing');
    } catch (error) {
      console.error('Error resuming audio:', error);
    }
    
    updateAudioButtons(tipId, clipName);
  };

  const updateAudioButtons = (tipId, clipName) => {
    const key = getAudioClipKey(tipId, clipName);
    const state = getAudioState(tipId, clipName);
    
    const playElement = document.querySelector(`[data-audio-key="${key}-play"]`);
    const pauseElement = document.querySelector(`[data-audio-key="${key}-pause"]`);
    const stopElement = document.querySelector(`[data-audio-key="${key}-stop"]`);
    
    const isPlaying = state === 'playing';
    const isPaused = state === 'paused';
    const isStopped = state === 'stopped' || state === 'loading' || state === 'error';
    
    // Update play button
    if (playElement) {
      if (isStopped || isPaused) {
        playElement.style.background = '#3b82f6';
        playElement.style.opacity = '1';
        playElement.style.cursor = 'pointer';
      } else {
        playElement.style.background = '#6b7280';
        playElement.style.opacity = '0.5';
        playElement.style.cursor = 'not-allowed';
      }
    }
    
    // Update pause button
    if (pauseElement) {
      if (isPlaying) {
        pauseElement.style.background = '#f59e0b';
        pauseElement.style.opacity = '1';
        pauseElement.style.cursor = 'pointer';
      } else {
        pauseElement.style.background = '#6b7280';
        pauseElement.style.opacity = '0.5';
        pauseElement.style.cursor = 'not-allowed';
      }
    }
    
    // Update stop button
    if (stopElement) {
      if (isPlaying || isPaused) {
        stopElement.style.background = '#dc2626';
        stopElement.style.opacity = '1';
        stopElement.style.cursor = 'pointer';
      } else {
        stopElement.style.background = '#6b7280';
        stopElement.style.opacity = '0.5';
        stopElement.style.cursor = 'not-allowed';
      }
    }
  };

  const updateStopButton = (tipId, clipName) => {
    // Legacy function - now handled by updateAudioButtons
    updateAudioButtons(tipId, clipName);
  };

  const loadOnScoreTips = async () => {
    if (!user?.id || !musicxmlId) return;
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const queryParams = new URLSearchParams({
        operation: 'getTips',
        userId: user.id,
        type: 'on-score',
        musicxmlId: musicxmlId,
        userRole: user.role || 'user'
      });
      
      const response = await fetch(`${API_BASE}/api/tips?${queryParams}`);
      
      if (response.ok) {
        const tipsData = await response.json();
        
        // Apply client-side filtering
        const filteredTips = TipTargeting.filterTipsForGeneralDisplay(tipsData, user, {
          currentPart: null // TODO: Get current part from context
        });
        
        // Convert tip positions to screen coordinates
        const tipsWithPositions = filteredTips.map(tip => {
          // Check for new coordinate format (pinX/pinY from ScorePositionSelector)
          if (tip.score_position && tip.score_position.pinX !== undefined && tip.score_position.pinY !== undefined) {
            return {
              ...tip,
              screenPosition: {
                x: tip.score_position.pinX,
                y: tip.score_position.pinY,
                valid: true
              },
              visible: true
            };
          }
          // Fallback to old coordinate system if available
          else if (tip.position && coordinateSystem) {
            const screenPos = coordinateSystem.musicalAnchorToScreen(tip.position, visibleParts);
            return {
              ...tip,
              screenPosition: screenPos,
              visible: screenPos.valid
            };
          }
          return { ...tip, visible: false };
        }).filter(tip => tip.visible);
        
        setTips(tipsWithPositions);
        
        // Notify parent component about tip availability
        if (onTipsLoaded) {
          onTipsLoaded({
            hasTips: tipsWithPositions.length > 0,
            tipCount: tipsWithPositions.length
          });
        }
      } else {
        // No tips found - still notify parent
        if (onTipsLoaded) {
          onTipsLoaded({
            hasTips: false,
            tipCount: 0
          });
        }
      }
    } catch (error) {
      console.error('Error loading on-score tips:', error);
      // Notify parent of error state
      if (onTipsLoaded) {
        onTipsLoaded({
          hasTips: false,
          tipCount: 0
        });
      }
    }
  };

  const updateTipPositions = useCallback(() => {
    setTips(prevTips => {
      return prevTips.map(tip => {
        // For new coordinate format (pinX/pinY), positions are fixed viewport coordinates
        if (tip.score_position && tip.score_position.pinX !== undefined && tip.score_position.pinY !== undefined) {
          return {
            ...tip,
            screenPosition: {
              x: tip.score_position.pinX,
              y: tip.score_position.pinY,
              valid: true
            },
            visible: true
          };
        }
        // For old coordinate system, update based on part visibility
        else if (coordinateSystem && tip.position) {
          const screenPos = coordinateSystem.musicalAnchorToScreen(tip.position, visibleParts);
          return {
            ...tip,
            screenPosition: screenPos,
            visible: screenPos.valid
          };
        }
        return tip;
      });
    });
  }, [coordinateSystem, visibleParts]);

  // Export coordinate system for external use
  const getCoordinateSystem = useCallback(() => {
    return coordinateSystem;
  }, [coordinateSystem]);

  // Function to add a new tip to the display (called from external components)
  const addNewTip = useCallback((newTip) => {
    if (coordinateSystem && newTip.position) {
      const screenPos = coordinateSystem.musicalAnchorToScreen(newTip.position, visibleParts);
      const tipWithScreenPos = {
        ...newTip,
        screenPosition: screenPos,
        visible: screenPos.valid
      };
      
      if (tipWithScreenPos.visible) {
        setTips(prevTips => [...prevTips, tipWithScreenPos]);
      }
    }
  }, [coordinateSystem, visibleParts]);

  const getPositionDescription = (tip) => {
    // Check for new coordinate format first
    if (tip.score_position && tip.score_position.measureNumber) {
      const position = tip.score_position;
      let desc = `${t('tips.measure')} ${position.measureNumber}`;
      
      if (position.beat !== undefined) {
        desc += `, ${t('tips.beat')} ${(position.beat * 4 + 1).toFixed(1)}`;
      }
      
      if (position.staffIndex !== undefined) {
        desc += `, Staff ${position.staffIndex + 1}`;
      }
      
      return desc;
    }
    // Fallback to old format
    else if (tip.position) {
      const position = tip.position;
      let desc = `${t('tips.measure')} ${position.measureNumber}`;
      
      if (position.beat !== undefined) {
        desc += `, ${t('tips.beat')} ${(position.beat * 4 + 1).toFixed(1)}`;
      }
      
      if (position.part) {
        desc += `, ${t('tips.part')} ${position.part}`;
      }
      
      return desc;
    }
    
    return '';
  };

  // Render tips directly into the score container
  useEffect(() => {
    // Clear any existing timeout
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    // Debounce the rendering to prevent rapid re-renders
    renderTimeoutRef.current = setTimeout(() => {
      if (!scoreDisplayRef?.current) {
        return;
      }

      const scoreContainer = scoreDisplayRef.current.getContainer();
      if (!scoreContainer) {
        return;
      }

      // Always clear any existing tip overlays first
      const existingOverlays = scoreContainer.querySelectorAll('.tip-overlay-element');
      existingOverlays.forEach(el => el.remove());

      // If not visible or no user, just clear and return
      if (!isVisible || !user) {
        return;
      }

      // Ensure score container clips overflow to prevent tips from escaping
      if (scoreContainer.style.overflow !== 'hidden') {
        scoreContainer.style.overflow = 'auto'; // Keep scrolling but clip content
      }

    // Debug display removed

    // Find the SVG element which contains the score content that scrolls
    const svgElement = scoreContainer.querySelector('svg');
    
    // For tips to scroll with the score, we need to append them to the SVG's parent
    // or directly to the score container, but positioned relative to the SVG content
    const targetElement = svgElement?.parentElement || scoreContainer;
    
    // Create tip pins
    tips.forEach((tip, index) => {      
      if (tip.visible && tip.screenPosition) {        
        let tipX, tipY;
        
        // Handle new coordinate format (pinX/pinY from ScorePositionSelector)
        if (tip.score_position && tip.score_position.pinX !== undefined && tip.score_position.pinY !== undefined) {
          // These are SVG-relative coordinates from modal, adjust for main score SVG positioning
          if (svgElement) {
            const svgRect = svgElement.getBoundingClientRect();
            const containerRect = targetElement.getBoundingClientRect();
            const mainScoreSvgOffsetX = svgRect.left - containerRect.left;
            const mainScoreSvgOffsetY = svgRect.top - containerRect.top;
            
            // Use stored modal SVG offset info to properly convert coordinates
            const modalSvgOffsetX = tip.score_position.modalSvgOffsetX || 0;
            const modalSvgOffsetY = tip.score_position.modalSvgOffsetY || 0;
            const offsetDiffX = mainScoreSvgOffsetX - modalSvgOffsetX;
            const offsetDiffY = mainScoreSvgOffsetY - modalSvgOffsetY;
            
            tipX = tip.score_position.pinX + mainScoreSvgOffsetX;
            tipY = tip.score_position.pinY + mainScoreSvgOffsetY;
          } else {
            tipX = tip.score_position.pinX;
            tipY = tip.score_position.pinY;
          }
          
          if (svgElement) {
            const svgRect = svgElement.getBoundingClientRect();
            const containerRect = targetElement.getBoundingClientRect();
           // console.log(`TIP DISPLAY: Tip ${tip.id} - stored SVG-relative(${tip.score_position.pinX}, ${tip.score_position.pinY}) -> final container-relative(${tipX}, ${tipY}). Main score SVG offset in container: (${svgRect.left - containerRect.left}, ${svgRect.top - containerRect.top})`);
          } else {
          //  console.log(`TIP DISPLAY: Tip ${tip.id} - stored SVG-relative(${tip.score_position.pinX}, ${tip.score_position.pinY}) -> final container-relative(${tipX}, ${tipY}) - no SVG found`);
          }
        } else {
          // Legacy coordinate format
          tipX = (typeof tip.screenPosition.x === 'number' && !isNaN(tip.screenPosition.x)) ? tip.screenPosition.x : 300;
          tipY = (typeof tip.screenPosition.y === 'number' && !isNaN(tip.screenPosition.y)) ? tip.screenPosition.y : 200;
        }
        
        const tipPin = document.createElement('div');
        tipPin.className = 'tip-overlay-element tip-pin';
        tipPin.setAttribute('data-tip-id', tip.id);
        
        // Apply dimmed styling if in placement mode
        const dimmedStyle = isInPlacementMode ? `
          opacity: 0.3;
          filter: grayscale(0.7) blur(0.5px);
          pointer-events: none;
        ` : '';
        
        tipPin.style.cssText = `
          position: absolute;
          left: ${tipX - 8}px;
          top: ${tipY - 8}px;
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          border-radius: 50%;
          border: 3px solid #ffffff;
          cursor: pointer;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
          transition: all 0.2s ease;
          pointer-events: auto;
          ${dimmedStyle}
        `;
        tipPin.textContent = '📍';
        
        // Create modern tooltip
        const tooltip = document.createElement('div');
        tooltip.className = 'modern-tip-tooltip';
        tooltip.setAttribute('data-tip-id', tip.id); // Also add tip ID to tooltip
        tooltip.style.cssText = `
          position: absolute;
          top: 40px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
          color: white;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 400;
          line-height: 1.5;
          max-width: 320px;
          min-width: 140px;
          white-space: pre-wrap;
          word-wrap: break-word;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(8px);
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 10000;
          pointer-events: auto;
        `;
        
        // Add arrow (pointing upward since tooltip is below)
        const arrow = document.createElement('div');
        arrow.style.cssText = `
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid #374151;
          filter: drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.2));
        `;
        tooltip.appendChild(arrow);
        
        // Render rich text content with audio support
        if (tip.content_html) {
          // Process audio markup in the HTML content and make audio clips interactive
          const audioRegex = /\[audio:([^\]]+)\]/g;
          let htmlContent = tip.content_html;
          let match;
          const audioClipsToProcess = [];
          
          while ((match = audioRegex.exec(tip.content_html)) !== null) {
            const clipName = match[1];
            const audioKey = getAudioClipKey(tip.id, clipName);
            audioClipsToProcess.push({ clipName, audioKey });
            
            // Get current state to show correct initial button
            const currentState = getAudioState(tip.id, clipName);
            let buttonText, buttonColor, buttonTitle;
            
            switch (currentState) {
              case 'playing':
                buttonText = '⏸️';
                buttonColor = '#16a34a';
                buttonTitle = `Pause: ${clipName}`;
                break;
              case 'paused':
                buttonText = '▶️';
                buttonColor = '#f59e0b';
                buttonTitle = `Resume: ${clipName}`;
                break;
              case 'error':
                buttonText = '❌';
                buttonColor = '#dc2626';
                buttonTitle = `Error: ${clipName}`;
                break;
              default: // stopped
                buttonText = '▶️';
                buttonColor = '#3b82f6';
                buttonTitle = `Play: ${clipName}`;
                break;
            }
            
            // Replace audio markup with fixed 3-button layout (play/pause/stop)
            const playButtonKey = `${audioKey}-play`;
            const pauseButtonKey = `${audioKey}-pause`;
            const stopButtonKey = `${audioKey}-stop`;
            
            // Determine button states based on current audio state
            const isPlaying = currentState === 'playing';
            const isPaused = currentState === 'paused';
            const isStopped = currentState === 'stopped' || currentState === 'loading' || currentState === 'error';
            
            const playButtonStyle = (isStopped || isPaused) ? 'background: #3b82f6; opacity: 1; cursor: pointer;' : 'background: #6b7280; opacity: 0.5; cursor: not-allowed;';
            const pauseButtonStyle = isPlaying ? 'background: #f59e0b; opacity: 1; cursor: pointer;' : 'background: #6b7280; opacity: 0.5; cursor: not-allowed;';
            const stopButtonStyle = (isPlaying || isPaused) ? 'background: #dc2626; opacity: 1; cursor: pointer;' : 'background: #6b7280; opacity: 0.5; cursor: not-allowed;';
            
            const audioIndicator = `
              <div style="display: inline-flex; align-items: center; gap: 4px; margin: 2px;">
                <div style="display: inline-flex; align-items: center; gap: 0; background: rgba(255,255,255,0.1); border-radius: 6px; padding: 1px;">
                  <div data-audio-key="${playButtonKey}" style="display: inline-block; ${playButtonStyle} color: white; padding: 4px 6px; border-radius: 5px 0 0 5px; font-size: 11px; border: none; transition: opacity 0.2s ease; min-width: 24px; text-align: center;" title="Play: ${clipName}">▶️</div>
                  <div data-audio-key="${pauseButtonKey}" style="display: inline-block; ${pauseButtonStyle} color: white; padding: 4px 6px; border-radius: 0; font-size: 11px; border: none; transition: opacity 0.2s ease; min-width: 24px; text-align: center;" title="Pause: ${clipName}">⏸️</div>
                  <div data-audio-key="${stopButtonKey}" style="display: inline-block; ${stopButtonStyle} color: white; padding: 4px 6px; border-radius: 0 5px 5px 0; font-size: 11px; border: none; transition: opacity 0.2s ease; min-width: 24px; text-align: center;" title="Stop: ${clipName}">⏹️</div>
                </div>
                <span style="color: rgba(255,255,255,0.9); font-size: 11px; font-weight: 500;">${clipName}</span>
              </div>
            `;
            htmlContent = htmlContent.replace(match[0], audioIndicator);
          }
          
          tooltip.innerHTML = htmlContent;
          
          // Add click handlers for audio clips after setting innerHTML
          audioClipsToProcess.forEach(({ clipName, audioKey }) => {
            // Play button
            const playElement = tooltip.querySelector(`[data-audio-key="${audioKey}-play"]`);
            if (playElement) {
              playElement.addEventListener('click', async (e) => {
                e.stopPropagation();
                const currentState = getAudioState(tip.id, clipName);
                if (currentState === 'stopped' || currentState === 'loading' || currentState === 'error') {
                  await handleAudioPlay(tip.id, clipName);
                } else if (currentState === 'paused') {
                  await handleAudioResume(tip.id, clipName);
                }
              });
            }
            
            // Pause button
            const pauseElement = tooltip.querySelector(`[data-audio-key="${audioKey}-pause"]`);
            if (pauseElement) {
              pauseElement.addEventListener('click', async (e) => {
                e.stopPropagation();
                const currentState = getAudioState(tip.id, clipName);
                if (currentState === 'playing') {
                  await handleAudioPause(tip.id, clipName);
                }
              });
            }
            
            // Stop button
            const stopElement = tooltip.querySelector(`[data-audio-key="${audioKey}-stop"]`);
            if (stopElement) {
              stopElement.addEventListener('click', async (e) => {
                e.stopPropagation();
                const currentState = getAudioState(tip.id, clipName);
                if (currentState === 'playing' || currentState === 'paused') {
                  await handleAudioStop(tip.id, clipName);
                }
              });
            }
          });
        } else {
          // Fallback to plain text for legacy content
          const audioRegex = /\[audio:([^\]]+)\]/g;
          let textContent = tip.content || '';
          
          // Replace audio markup with simple indicators in plain text
          textContent = textContent.replace(audioRegex, '🎵 $1');
          tooltip.textContent = textContent;
        }
        
        // Add creator info footer
        const creatorInfo = document.createElement('div');
        creatorInfo.style.cssText = `
          margin-top: 8px;
          padding-top: 6px;
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          font-size: 10px;
          color: rgba(255, 255, 255, 0.7);
          text-align: right;
        `;
        const creatorName = tip.creator_user_id === user?.id ? 'You' : 
                           (tip.creator_display_name || tip.creator_email || tip.creator_user_id);
        creatorInfo.textContent = `— ${creatorName}`;
        tooltip.appendChild(creatorInfo);
        
        // Add tooltip to pin
        tipPin.appendChild(tooltip);
        
        // Add hover effects with proper tooltip interaction
        let hideTimeout = null;
        
        const showTooltip = () => {
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }
          tipPin.style.transform = 'scale(1.3)';
          tooltip.style.opacity = '1';
          tooltip.style.visibility = 'visible';
          tooltip.style.transform = 'translateX(-50%) translateY(4px)';
        };
        
        const hideTooltip = () => {
          hideTimeout = setTimeout(() => {
            tipPin.style.transform = 'scale(1)';
            tooltip.style.opacity = '0';
            tooltip.style.visibility = 'hidden';
            tooltip.style.transform = 'translateX(-50%) translateY(0)';
          }, 100); // Small delay to allow mouse movement to tooltip
        };
        
        const cancelHide = () => {
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }
        };
        
        // Pin hover events
        tipPin.addEventListener('mouseenter', showTooltip);
        tipPin.addEventListener('mouseleave', hideTooltip);
        
        // Tooltip hover events to keep it open when hovering over content
        tooltip.addEventListener('mouseenter', cancelHide);
        tooltip.addEventListener('mouseleave', hideTooltip);

        // For proper scrolling, append to the target element (SVG parent or container)
        targetElement.appendChild(tipPin);
      }
    });
    
    }, 200); // 200ms debounce

    // Cleanup function
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [tips, isVisible, user, isInPlacementMode]); // Include placement mode for dimming

  // Cleanup audio instances when component unmounts or becomes invisible
  useEffect(() => {
    return () => {
      // Stop all audio when component unmounts
      audioInstancesRef.current.forEach((audio) => {
        if (audio && !audio.paused) {
          audio.pause();
        }
      });
    };
  }, []);

  // Pause all audio when overlay becomes invisible
  useEffect(() => {
    if (!isVisible) {
      audioInstancesRef.current.forEach((audio, key) => {
        if (audio && !audio.paused) {
          audio.pause();
          // Extract tipId and clipName from key to update state
          const parts = key.split('-');
          if (parts.length >= 2) {
            const tipId = parts[0];
            const clipName = parts.slice(1).join('-');
            setAudioState(tipId, clipName, 'paused');
          }
        }
      });
    }
  }, [isVisible]);

  // Don't render if not visible or no user
  if (!isVisible || !user) {
    return null;
  }

  // Don't render anything in React - we're using DOM manipulation
  return null;
};

export default OnScoreTipOverlay;